import {
  AuthApiResponse,
  ForgotPasswordResponse,
  GoogleSignInResult,
  OtpResponse,
} from "@/type/commonTypes";
import {
  serverAuthOTPSend,
  serverAuthOTPVerify,
  serverCheckEmailExists,
  serverCheckPhoneExists,
  serverCheckUserExists,
  serverForgotPassword,
  serverGoogleLoginCheck,
  serverLoginUser,
  serverRegisterGoogleUser,
  serverRegisterUser,
} from "./serverApiFile";

// Re-export server actions for consumers that wish to use them directly
export {
  serverAuthOTPSend,
  serverAuthOTPVerify,
  serverCheckEmailExists,
  serverCheckPhoneExists,
  serverCheckUserExists,
  serverForgotPassword,
  serverGoogleLoginCheck,
  serverLoginUser,
  serverRegisterGoogleUser,
  serverRegisterUser,
};

// ------------------ SESSION & COOKIE UTILS ------------------

export const setCookie = (
  name: string,
  value: string,
  days = 7,
  options: {
    secure?: boolean;
    sameSite?: "lax" | "strict" | "none";
    domain?: string;
  } = {
    secure: true,
    sameSite: "lax",
    domain: ".mysiponline.com",
  },
) => {
  if (typeof window === "undefined") return;

  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  let cookieStr = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;

  if (options.domain) {
    cookieStr += `; domain=${options.domain}`;
  }

  if (options.sameSite) {
    cookieStr += `; SameSite=${options.sameSite}`;
  }

  if (options.secure) {
    cookieStr += `; Secure`;
  }

  document.cookie = cookieStr;
};

export const getCookie = (name: string): string | null => {
  if (typeof window === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

export const deleteCookie = (
  name: string,
  options: { domain?: string; secure?: boolean } = {},
) => {
  let cookieStr = `${name}=; max-age=0; path=/`;
  if (options.domain) {
    cookieStr += `; domain=${options.domain}`;
  }
  if (options.secure) {
    cookieStr += `; Secure`;
  }
  document.cookie = cookieStr;
};

export const setSession = (key: string, value: string) => {
  sessionStorage.setItem(key, value);
};

export const getSession = (key: string): string | null => {
  return sessionStorage.getItem(key);
};

export const clearAuth = () => {
  sessionStorage.clear();
  deleteCookie("authToken", { domain: ".mysiponline.com", secure: true });
};

// ------------------ CHECK PHONE (SERVER-SIDE) ------------------

export const checkPhoneExists = async (phone: string): Promise<boolean> => {
  return serverCheckPhoneExists(phone);
};

// ------------------ CHECK EMAIL (SERVER-SIDE) ------------------

export const checkEmailExists = async (email: string): Promise<boolean> => {
  return serverCheckEmailExists(email);
};

// ------------------ LOGIN (SERVER-SIDE) ------------------

export const loginUser = async (
  email: string = "",
  phone: string = "",
  password: string = "",
): Promise<AuthApiResponse> => {
  const data = await serverLoginUser(email, phone, password);

  if (data.status === 1) {
    setCookie("authToken", data.authToken || "", 7);
    setCookie("token", data.token || "", 7);
  }

  return data;
};

// ------------------ REGISTER (SERVER-SIDE) ------------------

export const registerUser = async (
  name: string,
  email: string,
  phone: string,
  password: string,
  leadSource: string,
  buttonSource?: string | null,
): Promise<AuthApiResponse> => {
  const data = await serverRegisterUser(
    name,
    email,
    phone,
    password,
    leadSource,
    buttonSource,
  );

  if (data.status === 1) {
    setCookie("token", data.token || "", 7);
    setCookie("authToken", data.authToken || "", 7);
  }

  return data;
};

// ------------------ GOOGLE SIGN-IN ------------------

export const handleGoogleSignIn = (
  clientId: string,
  _csrfToken?: string,
): Promise<GoogleSignInResult> => {
  return new Promise<GoogleSignInResult>((resolve, reject) => {
    if (typeof window === "undefined") return reject("Window not defined");

    const loadGapi = () =>
      new Promise<void>((resolveLoad) => {
        if (window.gapi) return resolveLoad();
        const script = document.createElement("script");
        script.src = "https://apis.google.com/js/platform.js";
        script.async = true;
        script.defer = true;
        script.onload = () => resolveLoad();
        document.body.appendChild(script);
      });

    loadGapi().then(() => {
      window.gapi.load("auth2", () => {
        const auth2 = window.gapi.auth2.init({
          client_id: clientId,
          cookiepolicy: "single_host_origin",
        });

        auth2
          .signIn()
          .then((googleUser) => {
            const profile = googleUser.getBasicProfile() as {
              getEmail: () => string;
              getId: () => string;
              getName?: () => string;
            };
            const userEmail = profile.getEmail();
            const userName = profile.getName ? profile.getName() : "";
            const userGoogleId = profile.getId();

            // Delegate checking & login to server action (hidden from client network tab)
            serverGoogleLoginCheck(userGoogleId, userEmail)
              .then((result) => {
                if (result.status === 1) {
                  if (result.exists) {
                    setCookie("authToken", result.authToken || "", 7);
                    setCookie("token", result.token || "", 7);
                    resolve({
                      existOrNot: { status: 1 },
                      email: userEmail,
                      name: userName,
                      google_id: userGoogleId,
                    });
                  } else {
                    resolve({
                      existOrNot: { status: 0 },
                      email: userEmail,
                      name: userName,
                      google_id: userGoogleId,
                    });
                  }
                } else {
                  reject(result.message || "Google login failed");
                }
              })
              .catch(reject);
          })
          .catch(reject);
      });
    });
  });
};

// ------------------ HELPER TO GET AUTH ------------------

export const getAuthToken = () => {
  return getSession("authToken") || getCookie("authToken");
};

// ------------------ FORGOT PASSWORD FUNCTION (SERVER-SIDE) ------------------

export const forgotPassword = async (
  email: string,
): Promise<ForgotPasswordResponse> => {
  return serverForgotPassword(email);
};

// ------------------ OTP SEND (SERVER-SIDE) ------------------

export const authOTPSend = async ({
  mobile,
}: {
  mobile?: string;
}): Promise<OtpResponse> => {
  return serverAuthOTPSend({ mobile });
};

// ------------------ OTP VERIFY (SERVER-SIDE) ------------------

export const authOTPVerify = async ({
  otp,
  mobile,
  orderNo,
}: {
  otp: string;
  mobile?: string;
  orderNo?: string | number;
}): Promise<OtpResponse> => {
  return serverAuthOTPVerify({ otp, mobile, orderNo });
};
